import * as React from 'react';
import { lighten, styled } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker as MuiDatePicker, DatePickerProps as MuiDatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import preParsePostFormat from 'dayjs/plugin/preParsePostFormat';
import 'dayjs/locale/de';
import 'dayjs/locale/ar';
import { useTranslation } from '@askporter/config-provider';
import { IconSize, Icon } from '../Icon';

dayjs.extend(localeData);
dayjs.extend(preParsePostFormat);

interface DatePickerProps extends Omit<MuiDatePickerProps, 'onChange' | 'value' | 'maxDate' | 'minDate'> {
  value: string | null;
  onChange: (value: string) => void;
  label: string;
  maxDate?: string | null;
  minDate?: string | null;
  id?: string;
}

const StyledDatePicker = styled(MuiDatePicker)(({ theme }) => ({
  '.MuiFormControl-root': {
    marginTop: 0,
    marginBottom: 0,
  },
  '.MuiPickersInputBase-root': {
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#414651',
    WebkitTextFillColor: '#414651',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
    '&:focus-within': {
      boxShadow: `0 0 0 1px ${lighten(theme.palette.primary.main, 0.4)}, 0 0 0 4px ${lighten(
        theme.palette.primary.main,
        0.8,
      )}`,
      '&.Mui-error': {
        boxShadow: `0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(240, 68, 56, 0.24)`,
      },
      backgroundColor: '#fff',
      outline: 'none',
      borderRadius: '8px !important',
    },
    '& .MuiPickersSectionList-root': {
      padding: '12px 0',
      marginLeft: '6px',
    },
    flexDirection: 'row-reverse',
    '& .MuiInputAdornment-root': {
      marginLeft: 0,
      '& .MuiIconButton-root': {
        marginLeft: '-8px',
      },
    },
  },
}));

export const DatePicker: React.FC<DatePickerProps> = ({ value, onChange, label, maxDate, minDate, ...props }) => {
  const [val, setVal] = React.useState<Dayjs | null>(value ? dayjs(value) : null);
  const handleChange = (newValue: Dayjs | null) => {
    setVal(newValue);
    // Use 'en' locale for the output format to ensure ASCII digits (YYYY-MM-DD) regardless
    // of the display locale. RTL locales like Arabic use Arabic-Indic numerals in .format()
    // via the preParsePostFormat plugin, which would produce an unparseable date string.
    onChange(newValue ? newValue.locale('en').format('YYYY-MM-DD') : '');
  };
  const { i18n } = useTranslation();
  const adapterLocale = i18n.language ? i18n.language.split('-')[0] : 'en';
  const format = adapterLocale === 'de' ? 'DD. MMM YYYY' : 'DD MMM YYYY';

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      <StyledDatePicker
        value={val}
        onChange={handleChange}
        disabled={props.disabled}
        format={format}
        slots={{
          openPickerIcon: () => (
            <Icon size={IconSize.MD} folder="design-system/calendar" customStyles={{ pointerEvents: 'none' }} />
          ),
        }}
        slotProps={{
          textField: {
            inputProps: { 'aria-label': label },
            readOnly: props.readOnly,
            id: props.id,
            sx: { marginTop: 0, marginBottom: 0 },
          },
          openPickerButton: {
            'aria-label': label,
          },
          dialog: { 'aria-label': label },
          popper: { 'aria-label': label },
        }}
        maxDate={maxDate ? dayjs(maxDate) : undefined}
        minDate={minDate ? dayjs(minDate) : undefined}
        {...props}
      />
    </LocalizationProvider>
  );
};
